import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../hooks/useLocale';
import { formatPrice as fmtPrice } from '../utils/format';

// Load Stripe publishable key from environment variable
const getStripePublishableKey = () => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key || key === 'pk_test_your_stripe_publishable_key_here') return null;
  if (key.startsWith('sb_') || key.startsWith('sk_')) return null;
  return key;
};

const publishableKey = getStripePublishableKey();
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

export const CheckoutForm = ({ totalAmount, items, deliveryMethod, onSuccess, disabled = false, __testStripe, __testElements }) => {
  const stripeFromHook = useStripe();
  const elementsFromHook = useElements();
  const stripe = __testStripe !== undefined ? __testStripe : stripeFromHook;
  const elements = __testElements !== undefined ? __testElements : elementsFromHook;
  const locale = useLocale();
  const { t } = useTranslation('checkout');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (disabled) {
      setError(t('payment.missingInfo'));
      return;
    }
    
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment intent on your backend.
      // The server calculates the authoritative amount from DB prices —
      // we never send a client-supplied amount.
      let response;
      try {
        response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: (items || []).map(i => ({ id: i.id, quantity: i.quantity || 1, source: i.source || undefined })),
            deliveryMethod: deliveryMethod || 'delivery',
            currency: 'mxn',
          }),
        });
      } catch (fetchError) {
        console.error('Network error:', fetchError);
        throw new Error(t('payment.errors.network'));
      }

      // Clone response for error handling
      const responseClone = response.clone();
      const contentType = response.headers.get('content-type');

      if (!response.ok) {
        let errorMessage = t('payment.errors.serverGeneric', { status: response.status, statusText: response.statusText });
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch {
            const text = await responseClone.text().catch(() => 'Unknown error');
            errorMessage = `Error ${response.status}: ${text.substring(0, 100)}`;
          }
        } else {
          try {
            await response.text(); // consume body
            if (response.status === 404) {
              errorMessage = t('payment.errors.notFound');
            } else if (response.status === 500) {
              errorMessage = t('payment.errors.server500');
            } else {
              errorMessage = t('payment.errors.unknownStatus', { status: response.status });
            }
          } catch {
            errorMessage = t('payment.errors.serverGeneric', { status: response.status, statusText: response.statusText });
          }
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error(t('payment.errors.invalidResponse'));
      }

      if (!data || !data.clientSecret) {
        throw new Error(t('payment.errors.noClientSecret'));
      }

      const { clientSecret } = data;

      // Confirm payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || t('payment.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="form-group">
        <label className="form-label">{t('payment.cardLabel')}</label>
        <div style={{ 
          padding: '12px', 
          border: '1px solid #ddd', 
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div style={{ 
          color: '#dc3545', 
          marginBottom: '1rem',
          padding: '0.75rem',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      <button 
        type="submit" 
        className="btn btn-primary"
        disabled={!stripe || loading || disabled}
        style={{ 
          width: '100%', 
          padding: '1rem',
          opacity: (disabled || !stripe) ? 0.6 : 1,
          cursor: (disabled || !stripe) ? 'not-allowed' : 'pointer'
        }}
      >
        {loading ? (
          <>
            <div className="loading"></div>
            {t('payment.processing')}
          </>
        ) : (
          t('payment.payButton', { amount: fmtPrice(totalAmount, 'MXN', locale) })
        )}
      </button>
    </form>
  );
};

const StripeCheckout = ({ totalAmount, items, deliveryMethod, onSuccess, disabled = false }) => {
  if (!stripePromise) {
    const currentKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    const isSupabaseKey = currentKey.startsWith('sb_');

    // Dev-only configuration error — only fires when REACT_APP_STRIPE_PUBLISHABLE_KEY
    // is missing or malformed during local setup. Customers never see this.
    // Kept in Spanish on purpose (same rationale as TermsPage legal body).
    return (
      <div style={{
        padding: '2rem',
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        color: '#856404'
      }}>
        <h3 style={{ marginBottom: '1rem', color: '#856404' }}>⚠️ Error de Configuración de Stripe</h3>
        {isSupabaseKey ? (
          <>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Problema detectado:</strong> Estás usando una clave de Supabase en lugar de una clave de Stripe.
            </p>
            <p style={{ marginBottom: '0.5rem' }}>
              Las claves de <strong>Supabase</strong> comienzan con <code>sb_</code> (para la base de datos).
            </p>
            <p style={{ marginBottom: '0.5rem' }}>
              Las claves de <strong>Stripe</strong> comienzan con <code>pk_</code> (para pagos).
            </p>
            <p style={{ marginBottom: '1rem', padding: '1rem', background: '#fff', borderRadius: '4px' }}>
              <strong>✅ Cómo obtener tu clave de Stripe:</strong>
            </p>
            <ol style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Ve a <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ color: '#635BFF' }}>Stripe Dashboard → API Keys</a></li>
              <li style={{ marginBottom: '0.5rem' }}>Copia la <strong>Publishable key</strong> (debe comenzar con <code>pk_test_</code> o <code>pk_live_</code>)</li>
              <li style={{ marginBottom: '0.5rem' }}>Agrega esta clave a tu archivo <code>.env</code> como <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code></li>
              <li>Reinicia tu servidor de desarrollo</li>
            </ol>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
              <strong>Importante:</strong> No uses la clave SECRET (sk_) ni la clave de Supabase (sb_). Solo la clave PUBLISHABLE de Stripe (pk_).
            </p>
          </>
        ) : (
          <>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Problema:</strong> La clave pública de Stripe no está configurada correctamente.
            </p>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Solución:</strong> Por favor configura la variable de entorno <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> con una clave que comience con <code>pk_</code>.
            </p>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
              <strong>Nota:</strong> Asegúrate de usar la clave PUBLISHABLE (pk_) y NO la clave SECRET (sk_).
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        totalAmount={totalAmount}
        items={items}
        deliveryMethod={deliveryMethod}
        onSuccess={onSuccess}
        disabled={disabled}
      />
    </Elements>
  );
};

export default StripeCheckout;
